import { makeStyles } from 'tss-react/mui';
import React, { ReactElement } from 'react';
import palette from '@ui/palette';
import Typography from '@ui/Typography';
import { MuiButtonBase, MuiButtonBaseProps } from '@ui/base_library';
import { uppercaseFirst } from '../../../../../util/stringFormatting';

/**
 * IconButton currently only provides a button varient that transitions
 * the underlying SvgIcon betwen shades of grey/white. We will have to think this through
 * a bit further to support different color icon buttons.
 */

// TODO jss-to-tss-react codemod: Unable to handle style definition reliably. ArrowFunctionExpression in CSS prop.
const useStyles = makeStyles<{ label?: string }>()((theme, { label }) => ({
  root: {
    padding: label ? theme.spacing(1, 2) : theme.spacing(1),
    borderRadius: theme.spacing(0.5),
    '&:hover': {
      borderRadius: theme.spacing(0.5),
    },
    '&.Mui-focusVisible': {
      outline: `2px solid ${palette.mint_100}`,
    },
  },
  text: {
    marginLeft: theme.spacing(1),
    marginBottom: 2,
  },
  // Default (white/gray)
  colorDefault: {
    color: theme.palette.grey.A200,
    '&:hover': {
      color: theme.palette.common.white,
    },
    '&:active': {
      color: theme.palette.grey['500'],
    },
    '&.Mui-disabled, &.Mui-disabled:hover': {
      cursor: 'not-allowed',
      color: palette.gray_500,
    },
  },
  colorDefaultFocus: {
    color: theme.palette.common.white,
  },
  // Primary
  colorPrimary: {
    color: theme.palette.primary.main,
    '&:hover': {
      color: theme.palette.primary.light,
    },
    '&:active': {
      color: theme.palette.primary.dark,
    },
  },
  colorPrimaryFocus: {
    color: theme.palette.primary.light,
  },
  colorAction: {
    color: palette.gray_0,
    '&:hover, &.Mui-focusVisible': {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
    },
    '&[aria-disabled="true"], &.Mui-disabled, &.Mui-disabled:hover': {
      cursor: 'not-allowed',
      pointerEvents: 'auto',
      opacity: 0.5,
    },
  },
  chipBtn: {
    borderRadius: `12px !important`,
    backgroundColor: palette.gray_1000,
    padding: `${theme.spacing(1.5, 2)} !important`,
    '&:hover': {
      backgroundColor: `${palette.gray_900} !important`,
    },
    color: palette.gray_0,
    '&.Mui-focusVisible': {
      outlineOffset: 2,
    },
  },
}));

export interface IconButtonProps
  extends Omit<MuiButtonBaseProps, 'color' | 'aria-label'> {
  children: ReactElement;
  label?: string;
  'aria-label': string;
  color?: 'default' | 'primary' | 'action';
  variant?: 'chip';
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (props, forwardRef) => {
    const {
      className,
      children,
      color = 'default',
      label,
      variant,
      ...rest
    } = props;
    const { classes, cx } = useStyles({ label });
    // @ts-ignore Does not like the unkown key values
    const colorClassName = classes[`color${uppercaseFirst(color)}`];

    return (
      <MuiButtonBase
        ref={forwardRef}
        {...rest}
        className={cx(classes.root, colorClassName, className, {
          [classes.chipBtn]: variant === 'chip',
        })}
      >
        {React.cloneElement(children, { color: 'inherit' })}
        {label && (
          <Typography className={classes.text} variant="pMd" color="inherit">
            {label}
          </Typography>
        )}
      </MuiButtonBase>
    );
  }
);

export default IconButton;
